import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_META } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { initials } from "@/components/common";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, CalendarRange, GaugeCircle, FolderKanban, Settings as SettingsIcon,
  LogOut, Menu, X, ShieldCheck, DownloadCloud, History,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Today's Decisions", icon: LayoutDashboard, testid: "nav-today", end: true },
  { to: "/weekly", label: "Weekly Review", icon: CalendarRange, testid: "nav-weekly" },
  { to: "/accountability", label: "Accountability", icon: GaugeCircle, testid: "nav-accountability" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/import", label: "Import", icon: DownloadCloud, testid: "nav-import", roles: ["commercial", "admin"] },
  { to: "/audit", label: "Activity", icon: History, testid: "nav-audit", roles: ["admin", "ceo", "pmo"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout, roleMeta } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };

  const Sidebar = (
    <aside className="w-[264px] shrink-0 h-full flex flex-col border-r border-border bg-card/60 backdrop-blur">
      <NavLink to="/" onClick={() => setOpen(false)} data-testid="brand-home" className="px-5 py-5 border-b border-border flex items-center gap-3 hover:bg-muted/40 transition-colors">
        <img src="/polaris-emblem.png" alt="Polaris" className="w-11 h-11 object-contain" data-testid="brand-emblem" />
        <div className="leading-tight">
          <div className="font-heading text-lg tracking-tight">Polaris</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold/80">Priority Command</div>
        </div>
      </NavLink>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                isActive
                  ? "bg-gold/12 text-gold border border-gold/25"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60 border border-transparent"
              }`
            }
          >
            <n.icon size={18} strokeWidth={1.6} />
            <span className="font-medium">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-border">
        {roleMeta?.readonly && (
          <div className="flex items-center gap-1.5 text-[11px] text-gold/80 mb-3">
            <ShieldCheck size={13} /> Read-only executive view
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold text-xs font-semibold">
            {initials(user?.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{roleMeta?.label}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="p-2 rounded-md text-muted-foreground hover:text-critical hover:bg-critical/10 transition-colors"
            title="Sign out"
          >
            <LogOut size={17} />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <NotificationProvider>
    <div className="h-screen w-full flex polaris-bg overflow-hidden">
      <div className="hidden lg:flex h-full">{Sidebar}</div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative z-50 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="hidden lg:flex items-center justify-end gap-3 px-8 py-3 border-b border-border bg-card/40 backdrop-blur">
          <NotificationBell />
        </header>

        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card/70 backdrop-blur">
          <NavLink to="/" data-testid="brand-home-mobile" className="flex items-center gap-2">
            <img src="/polaris-emblem.png" alt="Polaris" className="w-8 h-8 object-contain" />
            <span className="font-heading text-base">Polaris</span>
          </NavLink>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 rounded-md hover:bg-muted">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</div>
        </main>
      </div>
    </div>
    </NotificationProvider>
  );
}
